import { HTMLCustomElement, dateUtils } from '@emartech/ui-framework-utils';
import hyperHTML from 'hyperhtml';
import Popper from 'popper.js';
import CustomEvent from 'custom-event';
import translator from '../../../utils/translator';
import configStore from '../../../utils/config-store';
import JSLogger from '../../../utils/jslogger';
const log = new JSLogger('decommission');
import mainTemplate from './template-main';
import pickerTemplate from './template-picker';

class EDaterangepicker extends HTMLCustomElement {
  init() {
    this._dom = {};
    this._dom.daterange = this._createDaterangeContainer();
    this._dom.picker = this._createDaterangeContainer();
    this._dom.wrapper = this._createWrapper();
    this._dom.picker.appendChild(this._dom.wrapper);
    this._dom.arrow = this._createArrowElement();

    this._events = {};
    this._events.open = this._open.bind(this);
    this._events.handleInputChange = this._handleInputChange.bind(this);
    this._events.cancel = this._cancel.bind(this);
    this._events.apply = this._apply.bind(this);
    this._events.clickOutside = this._clickOutside.bind(this);
    this._events.preset = this._setRangeFromPreset.bind(this);
    this._events.clear = this._clearDaterange.bind(this);

    this._state = {};
    this._state.layout = '';
    this._state.disabled = false;
    this._state.invalid = false;
    this._state.opened = false;
    this._state.inline = false;
    this._state.overlay = false;
    this._state.placement = 'bottom-start';
    this._state.borderStyle = '';
    this._state.start = { value: '', displayedValue: '', dateValue: null, originalValue: '', isoDate: '' };
    this._state.end = { value: '', displayedValue: '', dateValue: null, originalValue: '', isoDate: '' };
    this._state.defaultPresets = this._calculatePresets();
    this._state.presets = this._state.defaultPresets;
    this._state.customPresets = {};
    this._state.presetsHidden = false;

    this.addEventListener('update', this._handleCalendarUpdates.bind(this));
    this.addEventListener('datarangepreset.update', this._handlePresetUpdate.bind(this));
    this._dom.picker.addEventListener('update', this._handleCalendarUpdates.bind(this));
  }

  connectedCallback() {
    configStore.subscribe(this._configChangeCallback.bind(this));
    super._cleanupContainer('.e-daterange');
    this.appendChild(this._dom.daterange);
    this._render();
    log.log('EDaterangepicker');
  }

  disconnectedCallback() {
    this._close();
    this.innerHTML = '';
  }

  static get observedAttributes() {
    return [
      'start', 'end', 'layout', 'disabled', 'invalid', 'inline', 'overlay', 'min-date', 'max-date',
      'placement', 'no-clear', 'presets-hidden'
    ];
  }

  set start(value) {
    if (!value) {
      return;
    }
    this._setCalendarValues('start', value);
    this._render();

    if (this._state.layout === 'inline') {
      this.dispatchEvent(this._updateEvent());
    }
  }

  get start() {
    return this._state.start.value;
  }

  set end(value) {
    if (!value) {
      return;
    }

    this._setCalendarValues('end', value);
    this._render();

    if (this._state.layout === 'inline') {
      this.dispatchEvent(this._updateEvent());
    }
  }

  get end() {
    return this._state.end.value;
  }

  set layout(value) {
    this._state.layout = value;
    this._render();
  }

  set disabled(value) {
    this._state.disabled = super._convertAttributeToBoolean(value);
    this._render();
  }

  set invalid(value) {
    this._state.invalid = super._convertAttributeToBoolean(value);
    this._render();
  }

  set inline(value) {
    this._state.inline = super._convertAttributeToBoolean(value);
    this._dom.daterange.classList.toggle('e-daterange-inline', this._state.inline);
    this._render('inline');
  }

  set overlay(value) {
    this._state.overlay = super._convertAttributeToBoolean(value);
    this._render();
  }

  set minDate(value) {
    this._state.minDate = value;
    this._render();
  }

  set maxDate(value) {
    this._state.maxDate = value;
    this._render();
  }

  set placement(value) {
    this._state.placement = value;
    this._render();
  }

  set noClear(value) {
    this._state.noClear = super._convertAttributeToBoolean(value);
    this._render();
  }

  get value() {
    return this._getUpdateDetail();
  };

  set presetsHidden(value) {
    this._state.presetsHidden = super._convertAttributeToBoolean(value);
    this._render();
  }

  _configChangeCallback() {
    this._state.start.displayedValue = this._formatDateToDisplayedDate(this._state.start.value);
    this._state.end.displayedValue = this._formatDateToDisplayedDate(this._state.end.value);
    this._state.defaultPresets = this._calculatePresets();
    this._setRenderPreset();
    this._render();
  };

  _setCalendarValues(calendarType, value) {
    this._state[calendarType].value = value;
    this._state[calendarType].dateValue = dateUtils.parse(value);
    this._state[calendarType].displayedValue = this._formatDateToDisplayedDate(value);
    this._state[calendarType].isoDate = dateUtils.format(value, dateUtils.formats.fullDate);
  }

  _formatDateToDisplayedDate(value) {
    return dateUtils.formatByName(value, 'fullDate', {
      dateFormat: configStore.config.dateFormat,
      timeFormat: configStore.config.timeFormat
    });
  }

  _handleCalendarUpdates(event) {
    const type = event.target.getAttribute('type');
    const date = event.detail.date;
    const parsedDate = dateUtils.parse(date);

    if (type === 'start' && dateUtils.isAfter(parsedDate, this._state.end.dateValue)) {
      this.end = event.detail.date;
    }

    if (type === 'end' && dateUtils.isBefore(parsedDate, this._state.start.dateValue)) {
      this.end = this._state.start.value;
      return;
    }

    if (this[type] === date) {
      return;
    }

    this[type] = date;
  }

  _resetStart() {
    this._state.start = { value: '', displayedValue: '', dateValue: null, originalValue: '', isoDate: '' };
  }

  _resetEnd() {
    this._state.end = { value: '', displayedValue: '', dateValue: null, originalValue: '', isoDate: '' };
  }

  _setRenderPreset() {
    const hasCustomPreset = Object.keys(this._state.customPresets).length !== 0;
    this._state.presets = hasCustomPreset ? this._state.customPresets : this._state.defaultPresets;
  }

  _handlePresetUpdate(event) {
    this._state.customPresets[event.detail.uuid] = event.detail.data;
    this._setRenderPreset();
    event.target.addEventListener('datarangepreset.delete', this._handlePresetDelete.bind(this));
    this._render();
  }

  _handlePresetDelete(event) {
    delete this._state.customPresets[event.detail.uuid];
    this._setRenderPreset();
    this._render();
  }

  _render() {
    mainTemplate(hyperHTML.bind(this._dom.daterange), this._state, this._events);
    if (this._state.layout !== 'inline') {
      pickerTemplate(hyperHTML.bind(this._dom.wrapper), this._state, this._events);
    }
  }

  _open(event) {
    event.stopPropagation();
    if (this._state.opened || this._state.disabled) {
      this._close();
      return;
    }

    this._resetStyle(this._dom.picker);

    document.body.addEventListener('click', this._events.clickOutside);
    document.body.appendChild(this._dom.picker);

    this._storeOriginalValues();
    this._state.opened = true;
    this.dispatchEvent(this._openEvent());
    this._popper = this._initPopper();
    this._render();
  }

  _close() {
    document.body.removeEventListener('click', this._events.clickOutside);

    this._state.opened = false;
    this.dispatchEvent(this._closeEvent());
    this._destroyPopper();
    this._render();
  }

  _clickOutside(event) {
    if (event && event.target && this._dom.picker.contains(event.target)) {
      return;
    }
    this._restoreOriginalValues();
    this._close();
  }

  _apply() {
    const activeElement = document.activeElement;
    if (activeElement && activeElement.tagName === 'INPUT') {
      this._changeDate(activeElement.value, activeElement.getAttribute('date-type'));
    }

    this.dispatchEvent(this._updateEvent());
    this._close();
  }

  _cancel() {
    this._restoreOriginalValues();
    this._close();
  }

  _clearDaterange(event) {
    event.stopPropagation();
    this._state.start = { value: '', displayedValue: '', dateValue: null, originalValue: '' };
    this._state.end = { value: '', displayedValue: '', dateValue: null, originalValue: '' };
    this._render();
    this.dispatchEvent(this._updateEvent());
  }

  _resetStyle(element) {
    element.style.position = 'absolute';
    element.style.left = '-9999px';
  }

  _setRangeFromPreset(event) {
    event.stopPropagation();

    const start = event.target.getAttribute('data-start');
    const end = event.target.getAttribute('data-end');
    if (!start || !end || this._state.minDate > start || this._state.maxDate < end) {
      return;
    }

    this.start = start;
    this.end = end;
  }

  _handleInputChange(event) {
    const date = dateUtils.parse(this._convertToISODate(event.target.value));
    const type = event.target.getAttribute('date-type');
    const minDate = this._state.minDate ? dateUtils.parse(this._state.minDate) : undefined;
    if (date < minDate) {
      const formattedMinDate = dateUtils.format(minDate, dateUtils.formats.fullDate);
      event.target.value = this._formatDateToDisplayedDate(formattedMinDate);
      this[type] = formattedMinDate;
      return;
    }

    const maxDate = this._state.maxDate ? dateUtils.parse(this._state.maxDate) : undefined;
    if (date > maxDate) {
      const formattedMaxDate = dateUtils.format(maxDate, dateUtils.formats.fullDate);
      event.target.value = this._formatDateToDisplayedDate(formattedMaxDate);
      this[type] = formattedMaxDate;
      return;
    }

    this._changeDate(event.target.value, type);
  }

  _changeDate(value, type) {
    const date = dateUtils.parse(this._convertToISODate(value));
    this[type] = dateUtils.format(date, dateUtils.formats.fullDate);
  }

  _convertToISODate(value) {
    const formatTokens = configStore.config.dateFormat.match(/[a-zA-Z]+/g);
    const dateParts = value.match(/[0-9]+/g);
    if (!dateParts) {
      return;
    }

    const datePartsWithTokens = formatTokens.reduce((map, token, index) => {
      map[token] = dateParts[index];
      return map;
    }, {});

    return `${datePartsWithTokens.Y}-${datePartsWithTokens.m}-${datePartsWithTokens.d}`;
  }

  _storeOriginalValues() {
    this._state.start.originalValue = this._state.start.value;
    this._state.end.originalValue = this._state.end.value;
  }

  _restoreOriginalValues() {
    if (this._state.start.originalValue && this._state.end.originalValue) {
      this.start = this._state.start.originalValue;
      this.end = this._state.end.originalValue;
    } else {
      this._resetStart();
      this._resetEnd();
    }
  }

  _createDaterangeContainer() {
    const element = document.createElement('div');
    element.className = 'e-daterange';
    return element;
  }

  _createWrapper() {
    const element = document.createElement('div');
    return element;
  }

  _createArrowElement() {
    const element = document.createElement('div');
    element.className = 'e-popover__arrow';
    return element;
  }

  _initPopper() {
    this._dom.picker.classList.add('e-popover');
    this._dom.picker.appendChild(this._dom.arrow);

    return new Popper(this, this._dom.picker, {
      placement: this._state.placement,
      removeOnDestroy: true,
      modifiers: {
        arrow: {
          element: this._dom.arrow
        }
      }
    });
  }

  _destroyPopper() {
    if (!this._popper) {
      return;
    }

    this._popper.destroy();
    this._popper = null;
  }

  _updateEvent() {
    return new CustomEvent('update', {
      'detail': this._getUpdateDetail()
    });
  }

  _openEvent() {
    return new CustomEvent('open', {
      'detail': this._getUpdateDetail()
    });
  }

  _closeEvent() {
    return new CustomEvent('close');
  }

  _isOverlapped(data) {
    const height = data.offsets.reference.height;
    const direction = data.flipped ? 'bottom' : 'top';
    const offsetDifference = Math.abs(data.offsets.popper[direction] - data.offsets.reference[direction]);

    return offsetDifference !== height;
  }

  _calculatePresets() {
    const lastMonth = dateUtils.subMonths(new Date(), 1);

    return {
      1: {
        name: translator.translate('components.daterangePicker.presetDates.lastMonth'),
        start: dateUtils.format(dateUtils.startOfMonth(lastMonth), dateUtils.formats.fullDate),
        end: dateUtils.format(dateUtils.endOfMonth(lastMonth), dateUtils.formats.fullDate)
      },
      2: {
        name: translator.translate('components.daterangePicker.presetDates.lastSevenDays'),
        start: this._getLastXDays(7).start,
        end: this._getLastXDays(7).end
      },
      3: {
        name: translator.translate('components.daterangePicker.presetDates.lastThirtyDays'),
        start: this._getLastXDays(30).start,
        end: this._getLastXDays(30).end
      },
      4: {
        name: translator.translate('components.daterangePicker.presetDates.lastSixtyDays'),
        start: this._getLastXDays(60).start,
        end: this._getLastXDays(60).end
      },
      5: {
        name: translator.translate('components.daterangePicker.presetDates.lastNintyDays'),
        start: this._getLastXDays(90).start,
        end: this._getLastXDays(90).end
      }
    };
  }

  _getLastXDays(days) {
    return {
      start: dateUtils.format(dateUtils.subDays(new Date(), days - 1), dateUtils.formats.fullDate),
      end: dateUtils.format(new Date(), dateUtils.formats.fullDate)
    };
  }

  _getUpdateDetail() {
    return {
      'start': {
        'date': this._state.start.value,
        'dateObj': this._state.start.dateValue
      },
      end: {
        'date': this._state.end.value,
        'dateObj': this._state.end.dateValue
      }
    };
  }
}

export default EDaterangepicker;
